/**
 * Authenticated API Client
 * Automatically includes Azure AD access token in all requests
 */

import { msalInstance, loginRequest } from '../config/authConfig';

const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

/**
 * Get access token for API calls
 */
async function getAccessToken() {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error('No authenticated user');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return response.accessToken;
  } catch (error) {
    console.error('Failed to get access token:', error);
    throw error;
  }
}

/**
 * Make authenticated GET request
 */
export async function apiGet(endpoint, options = {}) {
  const token = await getAccessToken();

  const response = await fetch(`${backendUrl}${endpoint}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
}

/**
 * Make authenticated POST request
 */
export async function apiPost(endpoint, data, options = {}) {
  const token = await getAccessToken();

  const response = await fetch(`${backendUrl}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    body: JSON.stringify(data),
    ...options
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
}

/**
 * Make authenticated PUT request
 */
export async function apiPut(endpoint, data, options = {}) {
  const token = await getAccessToken();

  const response = await fetch(`${backendUrl}${endpoint}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    body: JSON.stringify(data),
    ...options
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
}

/**
 * Make authenticated DELETE request
 */
export async function apiDelete(endpoint, options = {}) {
  const token = await getAccessToken();

  const response = await fetch(`${backendUrl}${endpoint}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
}

export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  getAccessToken
};
